import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "../components/TopBar";
import Logo from "../components/Logo";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Printer, ChevronLeft } from "lucide-react";

/** Single-page tax-year recap. Designed to print to one page @ Letter/A4. */
export default function YearSummaryPostcard() {
  const { year } = useYear();
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [yields, setYields] = useState([]);

  useEffect(() => {
    api.get(`/reports/summary?year=${year}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    api.get(`/reports/field-pnl?year=${year}`).then((r) => setPnl(r.data)).catch(() => setPnl(null));
    api.get(`/yields?year=${year}`).then((r) => setYields(r.data)).catch(() => setYields([]));
  }, [year]);

  if (!summary) {
    return <div><TopBar title="Year Summary" subtitle="Loading…" /></div>;
  }

  // ------- derived rollups -------
  const bushelsByCrop = yields.reduce((acc, y) => {
    const k = y.crop || "—";
    acc[k] = (acc[k] || 0) + (y.quantity || 0);
    return acc;
  }, {});
  const cropRows = Object.entries(bushelsByCrop).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const topFields = (pnl?.fields || []).slice(0, 5);
  const categoryRows = Object.entries(summary.expenses_by_category || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Toolbar — hidden when printing */}
      <div className="no-print flex items-center justify-between mb-4 p-4 sm:px-6 lg:px-10 max-w-[8.5in] mx-auto">
        <Link to="/" data-testid="postcard-back-link" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#1F2A47] hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back to Dashboard
        </Link>
        <button
          data-testid="postcard-print-btn"
          onClick={() => window.print()}
          className="bg-[#1F2A47] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#15203A] inline-flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      {/* The postcard itself — strict width so print renders cleanly */}
      <div
        data-testid="year-summary-postcard"
        className="max-w-[8.5in] mx-auto bg-white border border-[#1F2A47] shadow-[8px_8px_0_0_#1F2A47] print:border-0 print:shadow-none"
      >
        <div className="bg-[#1F2A47] text-white px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={48} />
            <div>
              <div className="brand-tag text-2xl leading-none">ACRELATCH</div>
              <div className="text-[10px] tracking-[0.2em] text-[#C8C5B8] uppercase mt-1">Year in Review</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#C8C5B8]">Tax Year</div>
            <div className="font-display text-5xl font-black leading-none tabular">{year}</div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Headline numbers */}
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-[#E5E2D6] p-3">
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#66645E]">Revenue</div>
              <div className="font-display text-2xl font-black tabular text-[#1A4135]" data-testid="postcard-revenue">
                {fmtUSD(summary.total_revenue)}
              </div>
            </div>
            <div className="border border-[#E5E2D6] p-3">
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#66645E]">Net Expenses</div>
              <div className="font-display text-2xl font-black tabular text-[#BC4727]" data-testid="postcard-expenses">
                {fmtUSD(summary.net_expenses)}
              </div>
              {summary.partner_reimbursed > 0 && (
                <div className="text-[9px] text-[#66645E] mt-0.5">After {fmtUSD(summary.partner_reimbursed)} partner reimb.</div>
              )}
            </div>
            <div className={`p-3 ${summary.net_income >= 0 ? "bg-[#1A4135] text-white" : "bg-[#BC4727] text-white"}`}>
              <div className="text-[9px] uppercase tracking-[0.2em] font-bold opacity-80">Net Income</div>
              <div className="font-display text-2xl font-black tabular" data-testid="postcard-net-income">
                {fmtUSD(summary.net_income)}
              </div>
              <div className="text-[9px] opacity-80 mt-0.5">Rev − Exp − Mileage − Depreciation</div>
            </div>
          </div>

          {/* Deductions ribbon */}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="border-t-2 border-[#1F2A47] pt-1">
              <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#66645E]">Mileage Deduction</div>
              <div className="font-bold tabular">{fmtUSD(summary.mileage_deduction)} <span className="text-[#66645E] font-normal">({fmtNum(summary.total_miles_business, 0)} mi)</span></div>
            </div>
            <div className="border-t-2 border-[#1F2A47] pt-1">
              <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#66645E]">Depreciation</div>
              <div className="font-bold tabular">{fmtUSD(summary.depreciation)}</div>
            </div>
            <div className="border-t-2 border-[#1F2A47] pt-1">
              <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#66645E]">Activity</div>
              <div className="text-[11px] text-[#66645E]">
                {summary.yield_count} harvests · {summary.expense_count} exp · {summary.mileage_count} trips
              </div>
            </div>
          </div>

          {/* Two columns: yields + top fields */}
          <div className="grid grid-cols-2 gap-4">
            <section>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1F2A47] mb-2 pb-1 border-b-2 border-[#1F2A47]">
                Harvest by Crop
              </div>
              {cropRows.length === 0 ? (
                <div className="text-xs text-[#66645E] italic">No harvests logged.</div>
              ) : (
                <table className="w-full text-xs" data-testid="postcard-crops-table">
                  <tbody>
                    {cropRows.map(([crop, qty]) => (
                      <tr key={crop} className="border-b border-dashed border-[#E5E2D6]">
                        <td className="py-1 font-semibold">{crop}</td>
                        <td className="py-1 text-right tabular">{fmtNum(qty, 0)} <span className="text-[#66645E]">bu</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1F2A47] mb-2 pb-1 border-b-2 border-[#1F2A47]">
                Top Fields by Profit
              </div>
              {topFields.length === 0 ? (
                <div className="text-xs text-[#66645E] italic">No field-tagged activity yet.</div>
              ) : (
                <table className="w-full text-xs" data-testid="postcard-fields-table">
                  <tbody>
                    {topFields.map((f) => (
                      <tr key={f.field_id} className="border-b border-dashed border-[#E5E2D6]">
                        <td className="py-1 font-semibold">{f.name}</td>
                        <td className={`py-1 text-right tabular font-bold ${f.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                          {fmtUSD(f.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          {/* Expenses breakdown */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1F2A47] mb-2 pb-1 border-b-2 border-[#1F2A47]">
              Expenses by Category
            </div>
            {categoryRows.length === 0 ? (
              <div className="text-xs text-[#66645E] italic">No expenses logged.</div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="postcard-categories">
                {categoryRows.map(([cat, amt]) => {
                  const pct = summary.total_expenses > 0 ? (amt / summary.total_expenses) * 100 : 0;
                  return (
                    <div key={cat} className="flex justify-between border-b border-dashed border-[#E5E2D6] py-1">
                      <span className="capitalize">{cat.replace(/_/g, " ")}</span>
                      <span className="tabular font-bold">
                        {fmtUSD(amt)} <span className="text-[#66645E] font-normal">· {pct.toFixed(0)}%</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Footer */}
          <div className="pt-3 border-t border-[#E5E2D6] text-[9px] text-[#66645E] flex justify-between uppercase tracking-wider">
            <span>Generated {new Date().toISOString().slice(0, 10)} · AcreLatch</span>
            <span>For {year} tax prep — hand to your accountant</span>
          </div>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}
